import { useModal, create, show } from "@ebay/nice-modal-react";

import useTypedContext from "hooks/useTypedContext";
import FlashContext from "components/FlashMessages/FlashContext";
import DeleteConfirmationModal from "ds/components/DeleteConfirmationModal";
import { ConfirmationModalMessage } from "ds/components/ConfirmationModal/Message";
import useUserGroupRoleDelete from "shared/Role/useDeleteUserGroupRole";

import { UserManagementActivationStatus } from "../../types";

type UnassignConfirmationProps = {
  id: string;
  name: string;
  refetchQueries: string[];
  isLastRole: boolean;
  activationStatus: UserManagementActivationStatus;
};

const UnassignConfirmation = create(function UnassignConfirmation({
  id,
  name,
  refetchQueries,
  isLastRole,
  activationStatus,
}: UnassignConfirmationProps) {
  const { onError, reportSuccess } = useTypedContext(FlashContext);

  const modal = useModal();

  const [deleteUserGroupRole, { loading, data }] = useUserGroupRoleDelete({
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const onDelete = () => {
    deleteUserGroupRole({ id })
      .then((result) => {
        if (result?.data?.userGroupRoleBindingDelete?.id) {
          reportSuccess({ message: `${name} role successfully unassigned` });
          modal.resolve();
          modal.hide();
        }
      })
      .catch(onError);
  };

  const handleOnConfirm = () => {
    onDelete();
  };

  return (
    <DeleteConfirmationModal
      isLoading={loading}
      isDismissable={!loading && !data?.userGroupRoleBindingDelete?.id}
      title="Unassign role"
      confirmationButtonLabel="Unassign"
      onConfirm={handleOnConfirm}
    >
      <ConfirmationModalMessage>
        {isLastRole && activationStatus === UserManagementActivationStatus.ACTIVE ? (
          <>
            Unassigning <strong>{name}</strong> will revoke the IdP group's access to Spacelift
            resources.
            <br />
            <br />
            Are you sure you want to unassign this role?
          </>
        ) : (
          <>
            Are you sure you want to unassign the role <strong>{name}</strong>? You cannot undo this
            action.
          </>
        )}
      </ConfirmationModalMessage>
    </DeleteConfirmationModal>
  );
});

export const showUnassignConfirmation = (props: UnassignConfirmationProps) =>
  show(UnassignConfirmation, props);
